
if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      static get observedAttributes() {
        return ['data-gallery-mode'];
      }

      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.mobileDotButtons = Array.from(this.querySelectorAll('[data-media-dot-index]'));
        this.galleryScrollFrame = null;
        this.mediaExperimentDefaultState = null;
        this.mediaExperimentLayout = null;
        this.applyGalleryMode(this.dataset.galleryMode || 'video-first', {
          initializeVideo: false,
          reset: false,
        });
        this.elements.viewer?.addEventListener('slideChanged', this.onSlideChanged.bind(this));
        this.elements.viewer?.slider?.addEventListener('scroll', this.onGalleryScroll.bind(this), { passive: true });
        this.initMobilePagination();
        if (this.dataset.galleryMode !== 'images-only') this.initProductVideoAutoplayWhenReady();

        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch.querySelector('button').addEventListener('click', () => {
            this.productVideoPendingExitReason = 'thumbnail';
            this.setActiveMedia(mediaToSwitch.dataset.target, false);
          });
        });
        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        const isLocalPreview = ['localhost', '127.0.0.1'].includes(window.location.hostname);
        const isThemeEditor = Boolean(window.Shopify?.designMode);
        const isStaging = window.location.hostname === 'staging.kidsrideshotgun.com';
        if (!isLocalPreview && !isThemeEditor && !isStaging) return;

        const previewMode = new URLSearchParams(window.location.search).get('gallery-mode');
        if (previewMode === 'images-only' || previewMode === 'video-first') {
          this.setGalleryMode(previewMode);
        }
      }

      attributeChangedCallback(name, oldValue, newValue) {
        if (name !== 'data-gallery-mode' || oldValue === newValue || !this.elements) return;
        this.applyGalleryMode(newValue || 'video-first');
      }

      setGalleryMode(mode) {
        const normalizedMode = mode === 'images-only' ? 'images-only' : 'video-first';
        if (this.dataset.galleryMode === normalizedMode) {
          this.applyGalleryMode(normalizedMode);
          return;
        }
        this.dataset.galleryMode = normalizedMode;
      }

      normalizeShopifyMediaId(value) {
        const normalizedValue = String(value || '');
        const match = normalizedValue.match(/(?:^|-)(\d+)$/);
        return match ? match[1] : normalizedValue;
      }

      getElementShopifyMediaId(element) {
        if (!element) return '';
        return this.normalizeShopifyMediaId(
          element.dataset.shopifyMediaId ||
            element.dataset.mediaId ||
            element.dataset.mediaTarget ||
            element.dataset.target
        );
      }

      getMediaExperimentElements() {
        const sectionId = this.id.replace(/^MediaGallery-/, '').replace(/-duplicate$/, '');
        const productModal = document.getElementById(`ProductModal-${sectionId}`);
        return {
          viewer: Array.from(this.elements.viewer?.querySelectorAll('[id^="Slide-"]') || []),
          dots: Array.from(this.querySelectorAll('[data-media-dot-index]')),
          thumbnails: Array.from(this.elements.thumbnails?.querySelectorAll('[data-target]') || []),
          modal: Array.from(productModal?.querySelectorAll('.product-media-modal__content > [data-media-id]') || []),
        };
      }

      captureMediaExperimentDefaultState() {
        const elements = this.getMediaExperimentElements();
        if (!this.mediaExperimentDefaultState) {
          const featuredSlide = this.getFeaturedVideoSlide();
          const autoplayMedia = this.querySelector('[data-product-video-autoplay]');
          this.mediaExperimentDefaultState = {
            viewerOrder: elements.viewer.map((element) => this.getElementShopifyMediaId(element)),
            dotOrder: elements.dots.map((element) => this.getElementShopifyMediaId(element)),
            thumbnailOrder: elements.thumbnails.map((element) => this.getElementShopifyMediaId(element)),
            modalOrder: elements.modal.map((element) => this.getElementShopifyMediaId(element)),
            hiddenIds: new Set(
              elements.viewer.filter((element) => element.hidden).map((element) => this.getElementShopifyMediaId(element))
            ),
            featuredVideoId: this.getElementShopifyMediaId(featuredSlide),
            autoplayVideoId: this.normalizeShopifyMediaId(autoplayMedia?.dataset.mediaId),
          };
        } else if (!this.mediaExperimentDefaultState.modalOrder.length && elements.modal.length) {
          this.mediaExperimentDefaultState.modalOrder = elements.modal.map((element) =>
            this.getElementShopifyMediaId(element)
          );
        }
        return this.mediaExperimentDefaultState;
      }

      orderMediaExperimentElements(elements, preferredOrder, fallbackOrder) {
        if (!elements.length) return;
        const byId = new Map(elements.map((element) => [this.getElementShopifyMediaId(element), element]));
        const orderedIds = [...preferredOrder, ...fallbackOrder].filter(
          (mediaId, index, ids) => mediaId && byId.has(mediaId) && ids.indexOf(mediaId) === index
        );
        elements.forEach((element) => {
          const mediaId = this.getElementShopifyMediaId(element);
          if (!orderedIds.includes(mediaId)) orderedIds.push(mediaId);
        });
        const currentOrder = elements.map((element) => this.getElementShopifyMediaId(element));
        if (
          currentOrder.length === orderedIds.length &&
          currentOrder.every((mediaId, index) => mediaId === orderedIds[index])
        ) {
          return;
        }
        const parent = elements[0].parentElement;
        orderedIds.forEach((mediaId) => parent?.append(byId.get(mediaId)));
      }

      applyMediaExperimentVisibility(hiddenIds) {
        const elements = this.getMediaExperimentElements();
        Object.values(elements).forEach((collection) => {
          collection.forEach((element) => {
            this.setElementHidden(element, hiddenIds.has(this.getElementShopifyMediaId(element)));
          });
        });
      }

      setMediaExperimentMarkers(autoplayVideoId) {
        this.querySelectorAll('[data-featured-gallery-video]').forEach((element) => {
          element.removeAttribute('data-featured-gallery-video');
        });
        this.querySelectorAll('[data-featured-gallery-video-control]').forEach((element) => {
          element.removeAttribute('data-featured-gallery-video-control');
        });
        this.querySelectorAll('[data-product-video-autoplay]').forEach((element) => {
          element.removeAttribute('data-product-video-autoplay');
        });

        const elements = this.getMediaExperimentElements();
        const targetSlide = elements.viewer.find(
          (element) => this.getElementShopifyMediaId(element) === autoplayVideoId
        );
        const targetDot = elements.dots.find((element) => this.getElementShopifyMediaId(element) === autoplayVideoId);
        const targetThumbnail = elements.thumbnails.find(
          (element) => this.getElementShopifyMediaId(element) === autoplayVideoId
        );
        const targetDeferredMedia = targetSlide?.querySelector('deferred-media[data-media-id]');

        targetSlide?.setAttribute('data-featured-gallery-video', '');
        targetDot?.setAttribute('data-featured-gallery-video-control', '');
        targetThumbnail?.setAttribute('data-featured-gallery-video-control', '');
        targetDeferredMedia?.setAttribute('data-product-video-autoplay', '');
        return targetDeferredMedia || null;
      }

      setMediaExperimentLayout(config = {}) {
        const order = Array.isArray(config.order) ? config.order.map((id) => this.normalizeShopifyMediaId(id)) : [];
        const hidden = Array.isArray(config.hidden)
          ? new Set(config.hidden.map((id) => this.normalizeShopifyMediaId(id)))
          : new Set();
        const autoplayVideoId = this.normalizeShopifyMediaId(config.autoplayVideoId);
        if (!order.length || !autoplayVideoId) return false;
        const signature = JSON.stringify({ order, hidden: Array.from(hidden).sort(), autoplayVideoId });

        const defaultState = this.captureMediaExperimentDefaultState();
        const elements = this.getMediaExperimentElements();
        const availableIds = new Set(elements.viewer.map((element) => this.getElementShopifyMediaId(element)));
        if (!order.every((mediaId) => availableIds.has(mediaId)) || !availableIds.has(autoplayVideoId)) return false;

        if (this.mediaExperimentLayout?.signature === signature) {
          this.orderMediaExperimentElements(elements.viewer, order, defaultState.viewerOrder);
          this.orderMediaExperimentElements(elements.dots, order, defaultState.dotOrder);
          this.orderMediaExperimentElements(elements.thumbnails, order, defaultState.thumbnailOrder);
          this.orderMediaExperimentElements(elements.modal, order, defaultState.modalOrder);
          this.applyMediaExperimentVisibility(hidden);
          this.updateGalleryCounters(this.getVisibleMediaItems()[0]?.dataset.mediaId);
          return true;
        }

        this.destroyProductVideoAutoplay({ trackExit: true, exitReason: 'experiment_layout_change' });
        this.orderMediaExperimentElements(elements.viewer, order, defaultState.viewerOrder);
        this.orderMediaExperimentElements(elements.dots, order, defaultState.dotOrder);
        this.orderMediaExperimentElements(elements.thumbnails, order, defaultState.thumbnailOrder);
        this.orderMediaExperimentElements(elements.modal, order, defaultState.modalOrder);
        this.applyMediaExperimentVisibility(hidden);

        const targetDeferredMedia = this.setMediaExperimentMarkers(autoplayVideoId);
        this.mediaExperimentLayout = {
          order,
          hidden,
          autoplayVideoId,
          label: config.label || 'custom',
          signature,
        };
        this.dataset.mediaExperimentLayout = this.mediaExperimentLayout.label;
        this.mobileDotButtons = Array.from(this.querySelectorAll('[data-media-dot-index]'));
        this.setInitialVisibleMedia();
        this.updateGalleryCounters(this.getVisibleMediaItems()[0]?.dataset.mediaId);
        this.scheduleGalleryRefresh();

        const firstVisibleItem = this.getVisibleMediaItems()[0];
        const shouldPlay = Boolean(targetDeferredMedia && firstVisibleItem?.contains(targetDeferredMedia));
        this.initProductVideoAutoplayWhenReady(targetDeferredMedia, { shouldPlay });

        if (document.readyState === 'loading') {
          document.addEventListener(
            'DOMContentLoaded',
            () => {
              if (this.isConnected && this.mediaExperimentLayout) this.setMediaExperimentLayout(config);
            },
            { once: true }
          );
        }
        return true;
      }

      resetMediaExperimentLayout() {
        const defaultState = this.captureMediaExperimentDefaultState();
        const elements = this.getMediaExperimentElements();
        this.destroyProductVideoAutoplay({ trackExit: true, exitReason: 'experiment_layout_reset' });
        this.orderMediaExperimentElements(elements.viewer, defaultState.viewerOrder, []);
        this.orderMediaExperimentElements(elements.dots, defaultState.dotOrder, []);
        this.orderMediaExperimentElements(elements.thumbnails, defaultState.thumbnailOrder, []);
        this.orderMediaExperimentElements(elements.modal, defaultState.modalOrder, []);
        this.applyMediaExperimentVisibility(defaultState.hiddenIds);

        const defaultDeferredMedia = this.setMediaExperimentMarkers(defaultState.autoplayVideoId);
        const defaultFeaturedSlide = elements.viewer.find(
          (element) => this.getElementShopifyMediaId(element) === defaultState.featuredVideoId
        );
        defaultFeaturedSlide?.setAttribute('data-featured-gallery-video', '');
        delete this.dataset.mediaExperimentLayout;
        this.mediaExperimentLayout = null;
        this.mobileDotButtons = Array.from(this.querySelectorAll('[data-media-dot-index]'));
        this.setInitialVisibleMedia();
        this.updateGalleryCounters(this.getVisibleMediaItems()[0]?.dataset.mediaId);
        this.scheduleGalleryRefresh();
        const firstVisibleItem = this.getVisibleMediaItems()[0];
        this.initProductVideoAutoplayWhenReady(defaultDeferredMedia, {
          shouldPlay: Boolean(defaultDeferredMedia && firstVisibleItem?.contains(defaultDeferredMedia)),
        });
        return true;
      }

      applyGalleryMode(mode, options = {}) {
        const normalizedMode = mode === 'images-only' ? 'images-only' : 'video-first';
        const hideFeaturedVideo = normalizedMode === 'images-only';
        const featuredVideoSlide = this.getFeaturedVideoSlide();

        this.galleryMode = normalizedMode;
        if (hideFeaturedVideo) {
          this.cancelProductVideoImpressionTimer();
        } else {
          this.scheduleProductVideoImpression();
        }
        if (!featuredVideoSlide) {
          if (options.reset !== false) this.scheduleGalleryRefresh();
          return;
        }

        this.setElementHidden(featuredVideoSlide, hideFeaturedVideo);

        const featuredMediaId = featuredVideoSlide.dataset.mediaId;
        const featuredDot = this.mobileDotButtons.find((dotButton) => dotButton.dataset.mediaTarget === featuredMediaId);
        this.setElementHidden(featuredDot, hideFeaturedVideo);

        const featuredThumbnail = this.elements.thumbnails?.querySelector(`[data-target="${featuredMediaId}"]`);
        this.setElementHidden(featuredThumbnail, hideFeaturedVideo);

        this.setRelatedModalMediaVisibility(featuredVideoSlide, !hideFeaturedVideo);
        this.setInitialVisibleMedia();
        this.refreshMobilePagination(this.getVisibleMediaItems()[0]?.dataset.mediaId);

        if (hideFeaturedVideo) {
          if (this.productVideoAutoplay) {
            this.productVideoAutoplay.shouldPlay = false;
            this.setProductVideoPlaying(false);
          } else {
            featuredVideoSlide.querySelector('video')?.pause();
          }
        } else if (options.initializeVideo !== false && !this.productVideoAutoplay) {
          this.initProductVideoAutoplayWhenReady();
        }

        if (options.reset !== false) this.scheduleGalleryRefresh();
      }

      getFeaturedVideoSlide() {
        return (
          this.querySelector('[data-featured-gallery-video]') ||
          this.querySelector('[data-product-video-autoplay]')?.closest('.product__media-item')
        );
      }

      getVisibleMediaItems() {
        return Array.from(this.elements.viewer?.querySelectorAll('[id^="Slide-"]') || []).filter(
          (element) => !element.hidden
        );
      }

      getVisibleMobileDots() {
        const visibleMediaIds = new Set(this.getVisibleMediaItems().map((mediaItem) => mediaItem.dataset.mediaId));
        return this.mobileDotButtons.filter(
          (dotButton) => !dotButton.hidden && visibleMediaIds.has(dotButton.dataset.mediaTarget)
        );
      }

      setElementHidden(element, shouldHide) {
        if (!element) return;

        element.hidden = shouldHide;
        if (shouldHide) {
          element.setAttribute('aria-hidden', 'true');
          element.setAttribute('inert', '');
        } else {
          element.removeAttribute('aria-hidden');
          element.removeAttribute('inert');
        }
      }

      setRelatedModalMediaVisibility(featuredVideoSlide, isVisible) {
        const deferredMedia = featuredVideoSlide.querySelector('[data-product-video-autoplay]');
        const mediaId = deferredMedia?.dataset.mediaId;
        if (!mediaId) return;

        const sectionId = this.id.replace(/^MediaGallery-/, '').replace(/-duplicate$/, '');
        const productModal = document.getElementById(`ProductModal-${sectionId}`);
        if (!productModal) return;

        const modalMediaItem = Array.from(
          productModal.querySelectorAll('.product-media-modal__content > [data-media-id]')
        ).find((item) => item.dataset.mediaId === mediaId);
        if (!modalMediaItem) return;

        this.setElementHidden(modalMediaItem, !isVisible);
      }

      setInitialVisibleMedia() {
        const visibleItems = this.getVisibleMediaItems();
        const firstVisibleItem = visibleItems[0];
        if (!firstVisibleItem) return;

        const featuredVideoSlide = this.getFeaturedVideoSlide();
        if (featuredVideoSlide && !featuredVideoSlide.dataset.galleryOriginalFirstMatch) {
          featuredVideoSlide.dataset.galleryOriginalFirstMatch = featuredVideoSlide.classList.contains(
            'product__media-item--first-match'
          )
            ? 'true'
            : 'false';
        }

        this.querySelectorAll('[data-gallery-promoted-first]').forEach((mediaItem) => {
          mediaItem.removeAttribute('data-gallery-promoted-first');
          mediaItem.classList.remove('product__media-item--first-match');
        });
        this.querySelectorAll('[data-gallery-promoted-image]').forEach((image) => {
          if (image.dataset.galleryOriginalFetchpriority === '__none__') image.removeAttribute('fetchpriority');
          else image.setAttribute('fetchpriority', image.dataset.galleryOriginalFetchpriority);
          if (image.dataset.galleryOriginalLoading === '__none__') image.removeAttribute('loading');
          else image.setAttribute('loading', image.dataset.galleryOriginalLoading);
          delete image.dataset.galleryOriginalFetchpriority;
          delete image.dataset.galleryOriginalLoading;
          image.removeAttribute('data-gallery-promoted-image');
        });

        if (featuredVideoSlide) {
          featuredVideoSlide.classList.toggle(
            'product__media-item--first-match',
            this.galleryMode !== 'images-only' && featuredVideoSlide.dataset.galleryOriginalFirstMatch === 'true'
          );
        }

        if (this.galleryMode === 'images-only' || this.mediaExperimentLayout) {
          firstVisibleItem.classList.add('product__media-item--first-match');
          firstVisibleItem.setAttribute('data-gallery-promoted-first', '');
          const promotedImage = firstVisibleItem.querySelector('img');
          if (promotedImage) {
            promotedImage.dataset.galleryOriginalFetchpriority =
              promotedImage.getAttribute('fetchpriority') ?? '__none__';
            promotedImage.dataset.galleryOriginalLoading = promotedImage.getAttribute('loading') ?? '__none__';
            promotedImage.setAttribute('fetchpriority', 'high');
            promotedImage.setAttribute('loading', 'eager');
            promotedImage.setAttribute('data-gallery-promoted-image', '');
          }
        }

        this.elements.viewer
          ?.querySelectorAll('[id^="Slide-"]')
          .forEach((mediaItem) => mediaItem.classList.remove('is-active'));
        firstVisibleItem.classList.add('is-active');

        if (this.elements.thumbnails) {
          this.elements.thumbnails
            .querySelectorAll('button')
            .forEach((button) => button.removeAttribute('aria-current'));
          this.elements.thumbnails
            .querySelector(`[data-target="${firstVisibleItem.dataset.mediaId}"] button`)
            ?.setAttribute('aria-current', 'true');
        }
      }

      scheduleGalleryRefresh() {
        window.cancelAnimationFrame(this.galleryRefreshFrame);
        this.galleryRefreshFrame = window.requestAnimationFrame(() => {
          this.galleryRefreshFrame = null;
          const firstVisibleItem = this.getVisibleMediaItems()[0];
          const sliderComponent = this.elements.viewer;

          if (sliderComponent?.slider) sliderComponent.slider.scrollTo({ left: 0, behavior: 'auto' });
          sliderComponent?.resetPages?.();
          this.refreshMobilePagination(firstVisibleItem?.dataset.mediaId);
          const firstVisibleThumbnail = this.elements.thumbnails?.querySelector(
            `[data-target="${firstVisibleItem?.dataset.mediaId}"]`
          );
          if (firstVisibleThumbnail) this.setActiveThumbnail(firstVisibleThumbnail);
          if (firstVisibleItem) this.syncProductVideoPlayback(firstVisibleItem);
        });
      }

      onSlideChanged(event) {
        const currentElement = event.detail?.currentElement;
        this.setActiveMobileDot(currentElement?.dataset?.mediaId, event.detail?.currentPage);
        this.syncProductVideoPlayback(currentElement);

        if (!this.elements.thumbnails || !event.detail?.currentElement?.dataset) {
          return;
        }

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      onGalleryScroll() {
        if (this.mql.matches || this.galleryScrollFrame) return;

        this.galleryScrollFrame = window.requestAnimationFrame(() => {
          this.galleryScrollFrame = null;
          const currentItem = this.getNearestVisibleMediaItem();
          if (!currentItem) return;

          if (
            this.productVideoAutoplay?.shouldPlay &&
            !currentItem.contains(this.productVideoAutoplay.deferredMedia)
          ) {
            this.productVideoPendingExitReason = 'swipe';
          }
          this.setActiveMobileDot(currentItem.dataset.mediaId);
          this.syncProductVideoPlayback(currentItem);
        });
      }

      getNearestVisibleMediaItem() {
        const slider = this.elements.viewer?.slider;
        const visibleItems = this.getVisibleMediaItems();
        const firstVisibleItem = visibleItems[0];
        if (!slider || !firstVisibleItem) return null;

        const scrollOrigin = firstVisibleItem.offsetLeft;
        return visibleItems.reduce((nearestItem, mediaItem) => {
          const mediaDistance = Math.abs(slider.scrollLeft - (mediaItem.offsetLeft - scrollOrigin));
          const nearestDistance = Math.abs(slider.scrollLeft - (nearestItem.offsetLeft - scrollOrigin));
          return mediaDistance < nearestDistance ? mediaItem : nearestItem;
        }, firstVisibleItem);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) return;
        const shouldSkipScroll = this.dataset.skipScrollOnce === 'true';
        if (shouldSkipScroll) this.dataset.skipScrollOnce = 'false';
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        // Use requestAnimationFrame to batch layout operations and prevent forced reflows
        requestAnimationFrame(() => {
          if (!shouldSkipScroll && this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          // Avoid jumping the viewport on variant changes (when prepend === true)
          if (!shouldSkipScroll && !prepend && (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked')) {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          }
        });
        this.playActiveMedia(activeMedia);
        this.syncProductVideoPlayback(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        // Use requestAnimationFrame to prevent forced reflow when accessing offsetLeft
        requestAnimationFrame(() => {
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
        });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia?.hasAttribute('data-product-video-autoplay') && deferredMedia.querySelector('video, iframe')) {
          return;
        }
        window.pauseAllMedia();
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      initProductVideoAutoplayWhenReady(
        deferredMedia = this.querySelector('[data-product-video-autoplay]'),
        options = {}
      ) {
        if (this.dataset.galleryMode === 'images-only' || this.productVideoAutoplay) return;
        if (!deferredMedia) return;

        if (deferredMedia.querySelector('video, iframe')) {
          this.initProductVideoAutoplay(deferredMedia, options);
          return;
        }

        if (typeof deferredMedia.loadContent === 'function') {
          this.initProductVideoAutoplay(deferredMedia, options);
          return;
        }

        window.customElements?.whenDefined('deferred-media').then(() => {
          if (this.isConnected && !this.productVideoAutoplay) {
            this.initProductVideoAutoplay(deferredMedia, options);
          }
        });
      }

      initProductVideoAutoplay(deferredMedia = this.querySelector('[data-product-video-autoplay]'), options = {}) {
        if (!deferredMedia) return;

        let player = deferredMedia.querySelector('video, iframe');
        if (!player) {
          if (typeof deferredMedia.loadContent !== 'function') return;
          deferredMedia.loadContent(false);
          player = deferredMedia.querySelector('video, iframe');
        }
        if (!player) return;

        const shouldPlay = options.shouldPlay !== false;
        const autoplayState = {
          deferredMedia,
          player,
          host: deferredMedia.dataset.videoHost || 'shopify',
          shouldPlay,
          isVisible: !deferredMedia.closest('[hidden]'),
          autoplayBlocked: false,
          tapPaused: false,
          isPlaying: player.nodeName === 'VIDEO' ? !player.paused : false,
        };
        this.productVideoAutoplay = autoplayState;
        this.initProductVideoTracking();

        this.productVideoTapHandler = this.toggleProductVideoPlayback.bind(this);
        deferredMedia.addEventListener('click', this.productVideoTapHandler);

        if (player.nodeName === 'VIDEO') {
          this.prepareNativeProductVideo(player, shouldPlay);
          player.addEventListener('play', () => {
            if (this.productVideoAutoplay !== autoplayState) return;
            autoplayState.autoplayBlocked = false;
            autoplayState.isPlaying = true;
            this.updateProductVideoActiveWatchTime();
            this.removeProductVideoInteractionFallback();
          });
          player.addEventListener('pause', () => {
            if (this.productVideoAutoplay !== autoplayState) return;
            this.updateProductVideoActiveWatchTime();
            autoplayState.isPlaying = false;
            this.updateProductVideoActiveWatchTime();
          });
          player.addEventListener('playing', () => {
            if (this.productVideoAutoplay !== autoplayState) return;
            this.markProductVideoStarted();
            this.onProductVideoPlaying();
          });
          player.addEventListener('timeupdate', () => {
            if (this.productVideoAutoplay !== autoplayState) return;
            this.updateProductVideoProgress(player.currentTime, player.duration);
          });
          player.addEventListener('durationchange', () => {
            if (this.productVideoAutoplay !== autoplayState) return;
            this.updateProductVideoProgress(player.currentTime, player.duration);
          });
          player.addEventListener('error', () => {
            if (this.productVideoAutoplay !== autoplayState) return;
            this.trackProductVideoError('playback_error', {
              error_code: player.error?.code || 'native_media_error',
              error_message: player.error?.message || '',
              media_error_code: player.error?.code || null,
              network_state: player.networkState,
              ready_state: player.readyState,
            });
          });
          player.addEventListener('loadedmetadata', this.retryProductVideoAutoplay.bind(this));
          player.addEventListener('canplay', this.retryProductVideoAutoplay.bind(this));
          if (shouldPlay && !player.paused) {
            this.markProductVideoStarted();
            this.onProductVideoPlaying();
          }
        } else {
          this.productVideoMessageHandler = this.onProductVideoMessage.bind(this);
          window.addEventListener('message', this.productVideoMessageHandler);
          player.addEventListener(
            'load',
            () => {
              if (this.productVideoAutoplay !== autoplayState) return;
              this.registerExternalProductVideoEvents();
              this.setProductVideoPlaying(shouldPlay);
            },
            { once: true }
          );
          this.configureExternalProductVideo(player, this.productVideoAutoplay.host);
        }

        this.setupProductVideoAutoplayRetries();
        this.scheduleProductVideoDelayedRetry();
        this.setProductVideoPlaying(shouldPlay);
      }

      destroyProductVideoAutoplay(options = {}) {
        const autoplay = this.productVideoAutoplay;
        if (options.trackExit && autoplay) {
          this.trackProductVideoExit(options.exitReason || 'video_target_change');
        }
        if (autoplay?.player?.nodeName === 'VIDEO') {
          autoplay.player.pause();
          this.prepareNativeProductVideo(autoplay.player, false);
        } else if (autoplay) {
          this.pauseProductVideoForVisibility();
        }

        window.clearTimeout(this.productVideoDelayedRetry);
        window.clearTimeout(this.productVideoAutoplayFailureTimer);
        window.clearTimeout(this.productVideoTracking?.impressionTimer);
        window.clearInterval(this.productVideoWatchTimer);
        if (this.productVideoMessageHandler) {
          window.removeEventListener('message', this.productVideoMessageHandler);
        }
        if (this.productVideoRetryHandler) {
          window.removeEventListener('pageshow', this.productVideoRetryHandler);
          document.removeEventListener('visibilitychange', this.productVideoRetryHandler);
        }
        this.productVideoObserver?.disconnect();
        this.productVideoTrackingObserver?.disconnect();
        if (this.productVideoVisibilityHandler) {
          document.removeEventListener('visibilitychange', this.productVideoVisibilityHandler);
        }
        this.removeProductVideoInteractionFallback();
        if (this.productVideoTapHandler && this.productVideoAutoplay?.deferredMedia) {
          this.productVideoAutoplay.deferredMedia.removeEventListener('click', this.productVideoTapHandler);
        }
        this.productVideoObserver = null;
        this.productVideoTrackingObserver = null;
        this.productVideoMessageHandler = null;
        this.productVideoRetryHandler = null;
        this.productVideoVisibilityHandler = null;
        this.productVideoTapHandler = null;
        this.productVideoTracking = null;
        this.productVideoAutoplay = null;
        this.productVideoWatchTimer = null;
        this.productVideoDelayedRetry = null;
        this.productVideoAutoplayFailureTimer = null;
      }

      disconnectedCallback() {
        window.cancelAnimationFrame(this.galleryRefreshFrame);
        this.destroyProductVideoAutoplay();
      }

      prepareNativeProductVideo(player, shouldAutoplay = true) {
        player.muted = true;
        player.defaultMuted = true;
        player.volume = 0;
        player.autoplay = shouldAutoplay;
        player.loop = true;
        player.controls = false;
        player.playsInline = true;
        if (shouldAutoplay) player.setAttribute('autoplay', '');
        else player.removeAttribute('autoplay');
        player.setAttribute('loop', '');
        player.setAttribute('muted', '');
        player.setAttribute('playsinline', '');
        player.setAttribute('webkit-playsinline', '');
        player.removeAttribute('controls');
      }

      setupProductVideoAutoplayRetries() {
        if (!this.productVideoAutoplay) return;

        this.productVideoRetryHandler = this.retryProductVideoAutoplay.bind(this);
        window.addEventListener('pageshow', this.productVideoRetryHandler);
        document.addEventListener('visibilitychange', this.productVideoRetryHandler);

        this.productVideoInteractionHandler = (event) => {
          if (this.productVideoAutoplay?.deferredMedia.contains(event.target)) return;
          this.retryProductVideoAutoplay();
        };
        document.addEventListener('pointerdown', this.productVideoInteractionHandler, {
          capture: true,
          passive: true,
        });
        document.addEventListener('touchstart', this.productVideoInteractionHandler, {
          capture: true,
          passive: true,
        });

        if ('IntersectionObserver' in window) {
          this.productVideoObserver = new IntersectionObserver(
            ([entry]) => {
              if (!this.productVideoAutoplay || !entry) return;
              this.productVideoAutoplay.isVisible = entry.isIntersecting;
              if (this.mediaExperimentLayout && this.mql.matches) {
                this.productVideoAutoplay.shouldPlay = entry.isIntersecting && entry.intersectionRatio >= 0.15;
              }
              if (entry.isIntersecting) {
                this.retryProductVideoAutoplay();
              } else {
                this.pauseProductVideoForVisibility();
              }
            },
            { threshold: 0.15 }
          );
          this.productVideoObserver.observe(this.productVideoAutoplay.deferredMedia);
        }
      }

      scheduleProductVideoDelayedRetry() {
        window.clearTimeout(this.productVideoDelayedRetry);
        this.productVideoDelayedRetry = window.setTimeout(() => {
          this.productVideoDelayedRetry = null;
          this.retryProductVideoAutoplay();
        }, 5000);
      }

      removeProductVideoInteractionFallback() {
        if (!this.productVideoInteractionHandler) return;
        document.removeEventListener('pointerdown', this.productVideoInteractionHandler, true);
        document.removeEventListener('touchstart', this.productVideoInteractionHandler, true);
        this.productVideoInteractionHandler = null;
      }

      retryProductVideoAutoplay() {
        if (!this.productVideoAutoplay || document.visibilityState === 'hidden') return;
        const { isVisible, shouldPlay, tapPaused } = this.productVideoAutoplay;
        if (!isVisible || !shouldPlay || tapPaused) return;
        this.setProductVideoPlaying(true);
      }

      pauseProductVideoForVisibility() {
        if (!this.productVideoAutoplay) return;
        const { player, host } = this.productVideoAutoplay;
        this.updateProductVideoActiveWatchTime();
        this.productVideoAutoplay.isPlaying = false;
        this.updateProductVideoActiveWatchTime();
        if (player.nodeName === 'VIDEO') {
          player.pause();
        } else if (host === 'youtube') {
          player.contentWindow?.postMessage(
            JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }),
            '*'
          );
        } else if (host === 'vimeo') {
          player.contentWindow?.postMessage({ method: 'pause' }, '*');
        }
      }

      configureExternalProductVideo(player, host) {
        try {
          const url = new URL(player.getAttribute('src'), window.location.href);
          url.searchParams.set('autoplay', '1');
          url.searchParams.set('controls', '0');
          url.searchParams.set('loop', '1');
          url.searchParams.set('playsinline', '1');

          if (host === 'youtube') {
            url.searchParams.set('mute', '1');
            url.searchParams.set('enablejsapi', '1');
            url.searchParams.set('origin', window.location.origin);
          } else if (host === 'vimeo') {
            url.searchParams.set('muted', '1');
          }

          const allow = player.getAttribute('allow') || '';
          if (!allow.includes('autoplay')) {
            player.setAttribute('allow', `${allow}${allow ? '; ' : ''}autoplay`);
          }

          if (player.getAttribute('src') !== url.toString()) {
            player.setAttribute('src', url.toString());
          }
        } catch (_) {}
      }

      registerExternalProductVideoEvents() {
        if (!this.productVideoAutoplay) return;
        const { player, host } = this.productVideoAutoplay;

        if (host === 'youtube') {
          player.contentWindow?.postMessage(
            JSON.stringify({ event: 'listening', id: player.id || 'product-video' }),
            '*'
          );
        } else if (host === 'vimeo') {
          ['play', 'pause', 'ended', 'timeupdate', 'error'].forEach((eventName) => {
            player.contentWindow?.postMessage({ method: 'addEventListener', value: eventName }, '*');
          });
        }
      }

      onProductVideoMessage(event) {
        if (!this.productVideoAutoplay || event.source !== this.productVideoAutoplay.player.contentWindow) return;

        let data = event.data;
        if (typeof data === 'string') {
          try {
            data = JSON.parse(data);
          } catch (_) {
            return;
          }
        }

        const { host } = this.productVideoAutoplay;
        if (host === 'youtube') {
          if (data?.event === 'infoDelivery') {
            this.updateProductVideoProgress(data.info?.currentTime, data.info?.duration);
          }
          if (data?.event === 'onError') {
            this.trackProductVideoError('playback_error', {
              error_code: data.info || 'youtube_error',
              error_message: 'YouTube playback error',
            });
          }
          const playerState =
            data?.event === 'onStateChange'
              ? data.info
              : data?.event === 'infoDelivery'
                ? data.info?.playerState
                : undefined;
          if (playerState === 1) {
            this.productVideoAutoplay.autoplayBlocked = false;
            this.productVideoAutoplay.isPlaying = true;
            this.updateProductVideoActiveWatchTime();
            this.removeProductVideoInteractionFallback();
            this.markProductVideoStarted();
            this.onProductVideoPlaying();
          } else if (playerState === 0 || playerState === 2) {
            this.updateProductVideoActiveWatchTime();
            this.productVideoAutoplay.isPlaying = false;
            this.updateProductVideoActiveWatchTime();
          }
        } else if (host === 'vimeo') {
          if (data?.event === 'timeupdate') {
            this.updateProductVideoProgress(data.data?.seconds, data.data?.duration);
          }
          if (data?.event === 'error') {
            this.trackProductVideoError('playback_error', {
              error_code: data.data?.method || 'vimeo_error',
              error_message: data.data?.message || 'Vimeo playback error',
            });
          }
          if (data?.event === 'play') {
            this.productVideoAutoplay.autoplayBlocked = false;
            this.productVideoAutoplay.isPlaying = true;
            this.updateProductVideoActiveWatchTime();
            this.removeProductVideoInteractionFallback();
            this.markProductVideoStarted();
            this.onProductVideoPlaying();
          } else if (data?.event === 'pause' || data?.event === 'ended') {
            if (data?.event === 'ended') {
              this.updateProductVideoProgress(
                data.data?.seconds || this.productVideoTracking?.duration,
                data.data?.duration || this.productVideoTracking?.duration
              );
            }
            this.updateProductVideoActiveWatchTime();
            this.productVideoAutoplay.isPlaying = false;
            this.updateProductVideoActiveWatchTime();
          }
        }
      }

      toggleProductVideoPlayback() {
        if (!this.productVideoAutoplay || this.dataset.galleryMode === 'images-only') return;

        const { player, shouldPlay } = this.productVideoAutoplay;
        if (!shouldPlay) return;

        const isPlaying = player.nodeName === 'VIDEO' ? !player.paused && !player.ended : this.productVideoAutoplay.isPlaying;
        const interactionType = isPlaying ? 'tap_pause' : 'tap_play';
        if (!isPlaying && this.productVideoTracking && !this.productVideoTracking.hasPlayed) {
          this.productVideoTracking.startMethod = 'tap';
        }
        if (this.productVideoTracking) {
          this.productVideoTracking.interactionCount += 1;
        }
        this.trackProductVideoEvent('product_video_interaction', {
          event_action: interactionType,
          interaction_type: interactionType,
          interaction_number: this.productVideoTracking?.interactionCount || 1,
        });
        this.productVideoAutoplay.tapPaused = isPlaying;
        this.setProductVideoPlaying(true);
      }

      syncProductVideoPlayback(activeItem) {
        if (!this.productVideoAutoplay || !activeItem) return;
        const shouldPlay = activeItem.contains(this.productVideoAutoplay.deferredMedia);
        const wasSelected = this.productVideoAutoplay.shouldPlay;
        if (wasSelected && !shouldPlay && this.dataset.galleryMode !== 'images-only') {
          this.trackProductVideoExit(this.productVideoPendingExitReason || 'gallery_navigation');
        }
        this.productVideoPendingExitReason = null;
        this.productVideoAutoplay.shouldPlay = shouldPlay;
        this.setProductVideoPlaying(shouldPlay);
        if (shouldPlay) this.scheduleProductVideoImpression();
      }

      setProductVideoPlaying(shouldPlay) {
        if (!this.productVideoAutoplay) return;

        this.productVideoAutoplay.shouldPlay = shouldPlay;
        const { player, host, tapPaused } = this.productVideoAutoplay;
        const playbackShouldStart = shouldPlay && !tapPaused;

        if (player.nodeName === 'VIDEO') {
          if (playbackShouldStart) {
            this.prepareNativeProductVideo(player, true);
            const playPromise = player.play();
            if (playPromise?.then) {
              playPromise
                .then(() => {
                  this.productVideoAutoplay.autoplayBlocked = false;
                  this.productVideoAutoplay.isPlaying = true;
                  this.updateProductVideoActiveWatchTime();
                  this.removeProductVideoInteractionFallback();
                })
                .catch((error) => {
                  this.productVideoAutoplay.autoplayBlocked = true;
                  this.productVideoAutoplay.isPlaying = false;
                  this.productVideoAutoplay.lastPlayError = error;
                  if (this.productVideoTracking) {
                    this.productVideoTracking.autoplayHadFailure = true;
                  }
                  this.updateProductVideoActiveWatchTime();
                });
            }
          } else {
            this.updateProductVideoActiveWatchTime();
            this.prepareNativeProductVideo(player, false);
            player.pause();
            this.productVideoAutoplay.isPlaying = false;
            this.updateProductVideoActiveWatchTime();
          }
          return;
        }

        if (!playbackShouldStart) {
          this.updateProductVideoActiveWatchTime();
          this.productVideoAutoplay.isPlaying = false;
          this.updateProductVideoActiveWatchTime();
        }
        if (host === 'youtube') {
          player.contentWindow?.postMessage(
            JSON.stringify({ event: 'command', func: playbackShouldStart ? 'playVideo' : 'pauseVideo', args: [] }),
            '*'
          );
        } else if (host === 'vimeo') {
          player.contentWindow?.postMessage({ method: playbackShouldStart ? 'play' : 'pause' }, '*');
        }
      }

      initProductVideoTracking() {
        if (!this.productVideoAutoplay || this.productVideoTracking) return;

        this.productVideoTracking = {
          pageViewId: this.getProductVideoPageViewId(),
          visibilityRatio: 0,
          impressionTimer: null,
          impressionTracked: false,
          hasPlayed: false,
          startTracked: false,
          startMethod: 'autoplay',
          autoplayHadFailure: false,
          progressMilestones: new Set(),
          currentTime: 0,
          duration: 0,
          maxPercent: 0,
          activeWatchSeconds: 0,
          activeWatchStartedAt: null,
          lastCurrentTime: null,
          loopCount: 0,
          interactionCount: 0,
          exitCount: 0,
          trackedErrors: new Set(),
          autoplayFailureWindowElapsed: false,
        };

        this.productVideoVisibilityHandler = () => {
          this.updateProductVideoActiveWatchTime();
          if (document.visibilityState === 'hidden') {
            this.cancelProductVideoImpressionTimer();
          } else {
            this.scheduleProductVideoImpression();
          }
        };
        document.addEventListener('visibilitychange', this.productVideoVisibilityHandler);

        if ('IntersectionObserver' in window) {
          this.productVideoTrackingObserver = new IntersectionObserver(
            ([entry]) => {
              if (!this.productVideoTracking || !entry) return;
              this.updateProductVideoActiveWatchTime();
              this.productVideoTracking.visibilityRatio = entry.intersectionRatio;
              this.updateProductVideoActiveWatchTime();
              if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
                this.scheduleProductVideoImpression();
              } else {
                this.cancelProductVideoImpressionTimer();
              }
            },
            { threshold: [0, 0.5, 1] }
          );
          this.productVideoTrackingObserver.observe(this.productVideoAutoplay.deferredMedia);
        } else {
          this.productVideoTracking.visibilityRatio = 1;
          this.scheduleProductVideoImpression();
        }

        this.productVideoWatchTimer = window.setInterval(() => {
          this.updateProductVideoActiveWatchTime();
          const player = this.productVideoAutoplay?.player;
          if (player?.nodeName === 'VIDEO') {
            this.updateProductVideoProgress(player.currentTime, player.duration);
          }
        }, 250);
      }

      getProductVideoPageViewId() {
        if (!window.krsProductVideoPageViewId) {
          window.krsProductVideoPageViewId =
            window.crypto?.randomUUID?.() ||
            `pvid-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        }
        return window.krsProductVideoPageViewId;
      }

      scheduleProductVideoImpression() {
        const tracking = this.productVideoTracking;
        if (!tracking || tracking.impressionTracked || tracking.impressionTimer) return;
        if (!this.isProductVideoImpressionEligible()) return;

        tracking.impressionTimer = window.setTimeout(() => {
          tracking.impressionTimer = null;
          if (!this.isProductVideoImpressionEligible()) return;
          tracking.impressionTracked = true;
          this.trackProductVideoEvent('product_video_impression', {
            event_action: 'impression',
            visibility_threshold: 0.5,
            visibility_duration_ms: 1000,
          });
          if (tracking.hasPlayed) {
            this.trackProductVideoStart();
          } else {
            window.clearTimeout(this.productVideoAutoplayFailureTimer);
            this.productVideoAutoplayFailureTimer = window.setTimeout(() => {
              if (!this.productVideoTracking) return;
              this.productVideoTracking.autoplayFailureWindowElapsed = true;
              this.maybeTrackProductVideoAutoplayFailure();
            }, 8000);
          }
          this.updateProductVideoProgress(tracking.currentTime, tracking.duration);
          this.maybeTrackProductVideoAutoplayFailure();
        }, 1000);
      }

      cancelProductVideoImpressionTimer() {
        if (!this.productVideoTracking?.impressionTimer) return;
        window.clearTimeout(this.productVideoTracking.impressionTimer);
        this.productVideoTracking.impressionTimer = null;
      }

      isProductVideoImpressionEligible() {
        return Boolean(
          this.productVideoTracking &&
            this.dataset.galleryMode !== 'images-only' &&
            this.productVideoAutoplay?.shouldPlay &&
            document.visibilityState !== 'hidden' &&
            this.productVideoTracking.visibilityRatio >= 0.5
        );
      }

      onProductVideoPlaying() {
        const tracking = this.productVideoTracking;
        if (!tracking || this.dataset.galleryMode === 'images-only') return;

        tracking.hasPlayed = true;
        if (tracking.startMethod === 'autoplay' && tracking.autoplayHadFailure) {
          tracking.startMethod = 'autoplay_retry';
        }
        window.clearTimeout(this.productVideoAutoplayFailureTimer);
        this.productVideoAutoplayFailureTimer = null;
        this.updateProductVideoActiveWatchTime();
        if (tracking.impressionTracked) this.trackProductVideoStart();
      }

      trackProductVideoStart() {
        const tracking = this.productVideoTracking;
        if (!tracking || tracking.startTracked || !tracking.impressionTracked || !tracking.hasPlayed) return;

        tracking.startTracked = true;
        this.trackProductVideoEvent('product_video_start', {
          event_action: 'start',
          start_method: tracking.startMethod,
          autoplay_attempted: true,
          autoplay_success: tracking.startMethod === 'autoplay' && !tracking.autoplayHadFailure,
        });
        this.updateProductVideoProgress(tracking.currentTime, tracking.duration);
      }

      updateProductVideoProgress(currentTime, duration) {
        const tracking = this.productVideoTracking;
        if (!tracking || this.dataset.galleryMode === 'images-only') return;

        const nextCurrentTime = Number(currentTime);
        const nextDuration = Number(duration);
        if (Number.isFinite(nextCurrentTime) && nextCurrentTime >= 0) {
          if (
            Number.isFinite(nextDuration) &&
            nextDuration > 0 &&
            tracking.lastCurrentTime !== null &&
            tracking.lastCurrentTime > nextDuration * 0.75 &&
            nextCurrentTime < nextDuration * 0.25
          ) {
            tracking.loopCount += 1;
          }
          tracking.currentTime = nextCurrentTime;
          tracking.lastCurrentTime = nextCurrentTime;
        }
        if (Number.isFinite(nextDuration) && nextDuration > 0) {
          tracking.duration = nextDuration;
        }
        if (!tracking.duration) return;

        const percent = Math.min(100, Math.max(0, (tracking.currentTime / tracking.duration) * 100));
        tracking.maxPercent = Math.max(tracking.maxPercent, percent);
        if (!tracking.startTracked) return;

        [25, 50, 75, 100].forEach((milestone) => {
          const threshold = milestone === 100 ? 95 : milestone;
          if (percent < threshold || tracking.progressMilestones.has(milestone)) return;

          tracking.progressMilestones.add(milestone);
          this.trackProductVideoEvent('product_video_progress', {
            event_action: 'progress',
            video_percent: milestone,
            progress_milestone: milestone,
          });
        });
      }

      updateProductVideoActiveWatchTime() {
        const tracking = this.productVideoTracking;
        if (!tracking) return;

        const now = performance.now();
        if (tracking.activeWatchStartedAt !== null) {
          const elapsedSeconds = Math.min(1, Math.max(0, (now - tracking.activeWatchStartedAt) / 1000));
          tracking.activeWatchSeconds += elapsedSeconds;
        }
        tracking.activeWatchStartedAt = this.isProductVideoActivelyWatched() ? now : null;
      }

      isProductVideoActivelyWatched() {
        const autoplay = this.productVideoAutoplay;
        const tracking = this.productVideoTracking;
        if (!autoplay || !tracking) return false;

        const isActuallyPlaying =
          autoplay.player.nodeName === 'VIDEO'
            ? !autoplay.player.paused && !autoplay.player.ended
            : autoplay.isPlaying;
        return Boolean(
          this.dataset.galleryMode !== 'images-only' &&
            autoplay.shouldPlay &&
            isActuallyPlaying &&
            !autoplay.tapPaused &&
            document.visibilityState !== 'hidden' &&
            tracking.visibilityRatio >= 0.5
        );
      }

      trackProductVideoExit(exitReason) {
        const tracking = this.productVideoTracking;
        if (!tracking?.impressionTracked || this.dataset.galleryMode === 'images-only') return;

        tracking.exitCount += 1;
        this.trackProductVideoEvent('product_video_exit', {
          event_action: 'exit',
          exit_reason: exitReason,
          exit_number: tracking.exitCount,
        });
      }

      maybeTrackProductVideoAutoplayFailure() {
        const tracking = this.productVideoTracking;
        if (
          !tracking?.autoplayFailureWindowElapsed ||
          !tracking.impressionTracked ||
          tracking.hasPlayed ||
          tracking.startMethod === 'tap' ||
          !this.productVideoAutoplay?.shouldPlay ||
          this.productVideoAutoplay.tapPaused ||
          this.dataset.galleryMode === 'images-only'
        ) {
          return;
        }

        this.trackProductVideoError('autoplay_failed', {
          error_code: 'autoplay_failed_after_retry',
          error_message: this.productVideoAutoplay.lastPlayError?.message || '',
          autoplay_attempted: true,
          autoplay_success: false,
        });
      }

      trackProductVideoError(errorType, details = {}) {
        const tracking = this.productVideoTracking;
        if (!tracking || this.dataset.galleryMode === 'images-only') return;

        const errorKey = `${errorType}:${details.error_code || 'unknown'}`;
        if (tracking.trackedErrors.has(errorKey)) return;
        tracking.trackedErrors.add(errorKey);
        this.trackProductVideoEvent('product_video_error', {
          event_action: errorType,
          error_type: errorType,
          ...details,
        });
      }

      trackProductVideoEvent(eventName, eventData = {}) {
        if (!this.productVideoTracking || this.dataset.galleryMode === 'images-only') return;
        if (window.themeSettings?.gtm_enabled === false) return;

        this.updateProductVideoActiveWatchTime();
        const tracking = this.productVideoTracking;
        const productData = this.getProductVideoProductData();
        const experiment = this.getProductVideoExperimentContext();
        const videoId = this.productVideoAutoplay?.deferredMedia.dataset.mediaId || '';
        const currentPercent = tracking.duration
          ? Math.min(100, Math.max(0, (tracking.currentTime / tracking.duration) * 100))
          : 0;
        const payload = {
          event: eventName,
          event_category: 'product_video',
          event_action: eventName.replace('product_video_', ''),
          event_label: videoId,
          product_id: productData?.id?.toString?.() || '',
          product_title: productData?.title || '',
          product_handle: this.dataset.productHandle || productData?.handle || '',
          variant_id: this.getProductVideoVariantId(),
          video_id: videoId,
          video_position: this.getProductVideoPosition(),
          video_host: this.productVideoAutoplay?.host || 'shopify',
          video_duration_seconds: this.roundProductVideoMetric(tracking.duration),
          video_current_time_seconds: this.roundProductVideoMetric(tracking.currentTime),
          video_percent: this.roundProductVideoMetric(currentPercent),
          max_percent: this.roundProductVideoMetric(tracking.maxPercent),
          active_watch_seconds: this.roundProductVideoMetric(tracking.activeWatchSeconds),
          loop_count: tracking.loopCount,
          autoplay: true,
          muted: true,
          gallery_mode: this.dataset.galleryMode || 'video-first',
          media_experiment_layout: this.dataset.mediaExperimentLayout || 'default',
          experiment_id: experiment.experimentId,
          variation_id: experiment.variationId,
          experiment_variant: experiment.variant,
          store_region: experiment.storeRegion,
          environment: experiment.environment,
          device_category: this.getProductVideoDeviceCategory(),
          page_view_id: tracking.pageViewId,
          ...eventData,
        };

        const trackingManager = window.GTMTracking || window.UniversalTracking;
        if (typeof trackingManager?.track === 'function') {
          trackingManager.track(payload);
          return;
        }

        const enrichedPayload = {
          ...payload,
          page_type: 'product',
          page_url: window.location.href,
          page_title: document.title,
          page_path: window.location.pathname,
          page_search: window.location.search,
          shop_currency: window.Shopify?.currency?.active || '',
          shop_id: window.Shopify?.shop || '',
          _timestamp: Date.now(),
        };
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(enrichedPayload);
        document.dispatchEvent(new CustomEvent('tracking:universal', { detail: enrichedPayload }));
      }

      getProductVideoProductData() {
        if (this.productVideoProductData) return this.productVideoProductData;

        const productJson =
          this.closest('.shopify-section')?.querySelector('[data-product-json]') ||
          document.querySelector('[data-product-json]');
        if (!productJson) return null;

        try {
          this.productVideoProductData = JSON.parse(productJson.textContent);
          return this.productVideoProductData;
        } catch (_) {
          return null;
        }
      }

      getProductVideoVariantId() {
        const sectionId = this.id.replace(/^MediaGallery-/, '').replace(/-duplicate$/, '');
        const productInfo = document.getElementById(`ProductInfo-${sectionId}`);
        const variantInput =
          productInfo?.querySelector('input[name="id"]') ||
          productInfo?.querySelector('select[name="id"]') ||
          document.querySelector('product-form input[name="id"]');
        return (
          variantInput?.value?.toString() ||
          window.ShopifyAnalytics?.meta?.selectedVariantId?.toString() ||
          ''
        );
      }

      getProductVideoPosition() {
        const videoSlide = this.productVideoAutoplay?.deferredMedia.closest('.product__media-item');
        const mediaItems = this.getVisibleMediaItems();
        const position = mediaItems.indexOf(videoSlide) + 1;
        return position > 0 ? position : 1;
      }

      getProductVideoExperimentContext() {
        const hostname = window.location.hostname.toLowerCase();
        let storeRegion = String(window.themeSettings?.store_region || '').toLowerCase();
        if (hostname === 'kidsrideshotgun.eu' || hostname.endsWith('.kidsrideshotgun.eu')) storeRegion = 'eu';
        else if (hostname === 'kidsrideshotgun.com' || hostname === 'www.kidsrideshotgun.com') storeRegion = 'us';
        else if (hostname === 'staging.kidsrideshotgun.com') storeRegion = 'us';
        else if (!storeRegion) storeRegion = 'unknown';

        const environment =
          hostname === 'staging.kidsrideshotgun.com' ||
          hostname === 'localhost' ||
          hostname === '127.0.0.1' ||
          Boolean(window.Shopify?.designMode)
            ? 'staging'
            : 'production';

        return {
          storeRegion,
          environment,
          experimentId: null,
          variationId: null,
          variant: 'not_applicable',
        };
      }

      getProductVideoDeviceCategory() {
        if (window.matchMedia('(max-width: 749px)').matches) return 'mobile';
        if (window.matchMedia('(max-width: 989px)').matches) return 'tablet';
        return 'desktop';
      }

      roundProductVideoMetric(value) {
        const numericValue = Number(value);
        return Number.isFinite(numericValue) ? Math.round(numericValue * 10) / 10 : 0;
      }

      markProductVideoStarted() {
        window.clearTimeout(this.productVideoDelayedRetry);
        this.productVideoDelayedRetry = null;
        this.productVideoAutoplay?.deferredMedia.classList.add('product-video-autoplay--started');
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      initMobilePagination() {
        if (!this.mobileDotButtons.length || !this.elements.viewer || !this.elements.viewer.slider) return;

        this.mobileDotButtons.forEach((dotButton) => {
          if (dotButton.dataset.mediaDotBound === 'true') return;
          dotButton.dataset.mediaDotBound = 'true';
          dotButton.addEventListener('click', () => {
            const mediaTarget = dotButton.dataset.mediaTarget;
            if (!mediaTarget) return;
            this.productVideoPendingExitReason = 'pagination_dot';
            this.goToMediaTarget(mediaTarget);
          });
        });

        this.refreshMobilePagination(this.getVisibleMediaItems()[0]?.dataset.mediaId);
      }

      goToMediaTarget(mediaTarget) {
        if (!this.elements.viewer || !this.elements.viewer.slider) return;

        if (typeof this.elements.viewer.initPages === 'function') {
          this.elements.viewer.initPages();
        }

        const items = this.getVisibleMediaItems();
        const targetSlide = items.find((item) => item.dataset.mediaId === mediaTarget);
        if (!targetSlide) return;

        const scrollOrigin = items[0]?.offsetLeft || 0;
        this.setActiveMobileDot(mediaTarget);
        this.elements.viewer.slider.scrollTo({
          left: Math.max(0, targetSlide.offsetLeft - scrollOrigin),
          behavior: 'smooth',
        });
      }

      refreshMobilePagination(activeMediaId) {
        const visibleDotButtons = this.getVisibleMobileDots();
        const total = visibleDotButtons.length;

        visibleDotButtons.forEach((dotButton, index) => {
          const position = index + 1;
          const label = dotButton.dataset.mediaDotLabel || 'Load slide';
          const ofLabel = dotButton.dataset.mediaDotOf || 'of';
          dotButton.dataset.mediaDotIndex = String(position);
          dotButton.setAttribute('aria-label', `${label} ${position} ${ofLabel} ${total}`);
        });

        this.setActiveMobileDot(activeMediaId, 1);
      }

      updateGalleryCounters(activeMediaId) {
        const visibleItems = this.getVisibleMediaItems();
        const total = visibleItems.length;
        const activeIndex = Math.max(
          0,
          visibleItems.findIndex((item) => item.dataset.mediaId === activeMediaId)
        );
        const currentCounter = this.querySelector('.slider-counter--current');
        const totalCounter = this.querySelector('.slider-counter--total');
        if (currentCounter) currentCounter.textContent = String(total ? activeIndex + 1 : 0);
        if (totalCounter) totalCounter.textContent = String(total);

        const visibleThumbnails = Array.from(this.elements.thumbnails?.querySelectorAll('[data-target]') || []).filter(
          (thumbnail) => !thumbnail.hidden
        );
        visibleThumbnails.forEach((thumbnail, index) => {
          const position = index + 1;
          thumbnail.dataset.mediaPosition = String(position);
          const labelTemplate = thumbnail.dataset.thumbnailLabelTemplate;
          const button = thumbnail.querySelector('button');
          if (button && labelTemplate) button.setAttribute('aria-label', labelTemplate.replace('__position__', position));
        });
      }

      setActiveMobileDot(activeMediaId, fallbackIndex = 1) {
        if (!this.mobileDotButtons.length) return;

        const visibleDotButtons = this.getVisibleMobileDots();
        const activeDot =
          visibleDotButtons.find((dotButton) => dotButton.dataset.mediaTarget === activeMediaId) ||
          visibleDotButtons[Math.max(0, Number(fallbackIndex || 1) - 1)];

        this.mobileDotButtons.forEach((dotButton) => {
          const isActive = dotButton === activeDot;
          dotButton.classList.toggle('product-media-gallery__dot--active', isActive);
          dotButton.setAttribute('aria-current', isActive ? 'true' : 'false');
        });
        this.updateGalleryCounters(activeDot?.dataset.mediaTarget);
      }
    }
  );
}
